import localforage from 'localforage';
import moment from 'moment';
import { dateTimeFormat } from './format';

export interface IPOSMenuImage {
  menuID: string;
  imageID: string;
  fileImage?: Blob;
  expireAt?: string;
}

const createLocalForage = ({
  name,
  version,
  storeName,
  description,
}: {
  name: string;
  version?: number;
  storeName: string;
  description: string;
}) => {
  return localforage.createInstance({
    driver: localforage.INDEXEDDB, // Use IndexedDB as the storage driver
    name, // Custom name for the database
    version: version ?? 1.0, // Version of the database
    storeName, // Custom store name for the grid state
    description, // Description of the store
  });
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const posLocalForage = () => {
  let listeners: Array<() => Promise<void>> = [];
  const prefix = 'pos-menu';
  const storeInstance = createLocalForage({
    name: 'pos-menus', // Custom name for the database
    version: 1.0, // Version of the database
    storeName: 'pos-menus-store', // Custom store name for the grid state
    description: 'POS Menu Image', // Description of the store
  });

  const getStorageKey = (stationID: string) => `${prefix}_${stationID}`;

  const defaultState: IPOSMenuImage[] = [];

  const getCachedState = async (stationID: string) =>
    (await storeInstance.getItem<IPOSMenuImage[]>(getStorageKey(stationID))) ||
    defaultState;

  const notifyListeners = async () => {
    await Promise.all(listeners.map((listener) => listener()));
  };

  return {
    subscribe: (callback: () => Promise<void>) => {
      listeners.push(callback);
      return () => {
        listeners = listeners.filter((listener) => listener !== callback);
      };
    },
    getSnapshot: (stationID: string) => getCachedState(stationID),
    setItem: async (
      stationID: string,
      value: IPOSMenuImage[] | IPOSMenuImage,
    ) => {
      const storageKey = getStorageKey(stationID);
      const currentState = await getCachedState(stationID);

      // Upsert array
      if (Array.isArray(value)) {
        const nextState: IPOSMenuImage[] = [...currentState];

        value.forEach((incoming) => {
          const index = nextState.findIndex(
            (item) => item.menuID === incoming.menuID,
          );

          if (index === -1) {
            nextState.push({
              ...incoming,
              fileImage: incoming.fileImage,
              expireAt: moment().add(12, 'hours').format(dateTimeFormat),
            });
          } else {
            nextState[index] = {
              ...nextState[index],
              ...incoming,
            };
          }
        });

        await storeInstance.setItem(storageKey, nextState);
        await notifyListeners();
        return;
      }

      // Upsert single item
      if (!isPlainObject(value) || !value.menuID) return;

      const index = currentState.findIndex(
        (item) => item.menuID === value.menuID,
      );

      const nextState: IPOSMenuImage[] =
        index === -1
          ? [
              ...currentState,
              {
                ...value,
                fileImage: value.fileImage,
                expireAt: moment().add(12, 'hours').format(dateTimeFormat),
              },
            ]
          : currentState.map((item) =>
              item.menuID === value.menuID
                ? {
                    ...item,
                    ...value,
                    expireAt:
                      item.expireAt ??
                      moment().add(12, 'hours').format(dateTimeFormat),
                  }
                : item,
            );

      await storeInstance.setItem(storageKey, nextState);
      await notifyListeners();
    },
    clearStore: async (stationID: string) => {
      await storeInstance.setItem(getStorageKey(stationID), []);
      await notifyListeners();
    },
  };
};
